import React, { useState } from 'react';
import { Award, CheckCircle2, Clock, DollarSign, Hourglass, Users } from 'lucide-react';

import { CurrencyDetailsModal } from '../CurrencyDetailsModal/CurrencyDetailsModal';

const getCardStyle = (type) => {
  switch (type) {
    case 'earned':
      return {
        bg: 'bg-gradient-to-br from-blue-500 to-blue-600',
        icon: <Award className="w-4 h-4 text-blue-200" />,
        tooltipIcon: <Award className="w-12 h-12 -rotate-12" />,
        tooltipText: 'Total rewards earned across all currencies',
        textColor: 'text-blue-100',
        iconColor: 'text-blue-200',
      };
    case 'pending':
      return {
        bg: 'bg-gradient-to-br from-amber-500 to-amber-600',
        icon: <Clock className="w-4 h-4 text-amber-200" />,
        tooltipIcon: <Clock className="w-12 h-12 -rotate-12" />,
        tooltipText: 'Pending rewards awaiting payment',
        textColor: 'text-amber-100',
        iconColor: 'text-amber-200',
      };
    case 'paid':
      return {
        bg: 'bg-gradient-to-br from-green-500 to-green-600',
        icon: <CheckCircle2 className="w-4 h-4 text-green-200" />,
        tooltipIcon: <CheckCircle2 className="w-12 h-12 -rotate-12" />,
        tooltipText: 'Total paid rewards',
        textColor: 'text-green-100',
        iconColor: 'text-green-200',
      };
    case 'potential':
      return {
        bg: 'bg-gradient-to-br from-purple-500 to-purple-600',
        icon: <Hourglass className="w-4 h-4 text-purple-200" />,
        tooltipIcon: <Hourglass className="w-12 h-12 -rotate-12" />,
        tooltipText: 'Potential rewards from pending referrals',
        textColor: 'text-purple-100',
        iconColor: 'text-purple-200',
      };
    case 'referrals':
      return {
        bg: 'bg-gradient-to-br from-indigo-500 to-indigo-600',
        icon: <Users className="w-4 h-4 text-indigo-200" />,
        tooltipIcon: <Users className="w-12 h-12 -rotate-12" />,
        tooltipText: 'Total number of referrals sent',
        textColor: 'text-indigo-100',
        iconColor: 'text-indigo-200',
      };
    case 'hires':
      return {
        bg: 'bg-gradient-to-br from-pink-500 to-pink-600',
        icon: <Users className="w-4 h-4 text-pink-200" />,
        tooltipIcon: <Users className="w-12 h-12 -rotate-12" />,
        tooltipText: 'Total number of successful hires',
        textColor: 'text-pink-100',
        iconColor: 'text-pink-200',
      };
    default:
      return {
        bg: 'bg-gradient-to-br from-gray-500 to-gray-600',
        icon: <DollarSign className="w-4 h-4 text-gray-200" />,
        tooltipIcon: <DollarSign className="w-12 h-12 -rotate-12" />,
        tooltipText: '',
        textColor: 'text-gray-100',
        iconColor: 'text-gray-200',
      };
  }
};

export const StatsCard = ({ title, amount, type, breakdown, prefferedCurrency }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { bg, icon, tooltipText, textColor, tooltipIcon } = getCardStyle(type);

  const hasCurrencyBreakdown = breakdown && breakdown.length > 0;

  const handleCardClick = () => {
    if (!hasCurrencyBreakdown) return;

    setIsModalOpen(true);
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && hasCurrencyBreakdown) {
      e.preventDefault();
      setIsModalOpen(true);
    }
  };

  return (
    <>
      <div className="relative group">
        <div
          className={`${bg} rounded-lg p-3 relative h-full ${
            hasCurrencyBreakdown ? 'cursor-pointer hover:opacity-90' : ''
          }`}
          {...(hasCurrencyBreakdown && {
            onClick: handleCardClick,
            onKeyDown: handleKeyDown,
            role: 'button',
            tabIndex: 0,
          })}
        >
          <div className="absolute bottom-0 right-0 overflow-hidden opacity-10">{tooltipIcon}</div>
          <div className="relative z-10 flex flex-col h-full">
            <div className="flex items-start justify-between">
              <h3 className={`text-xs font-medium ${textColor}`}>{title}</h3>
              {icon}
            </div>
            <div className="mt-auto text-xl font-bold text-white">{amount}</div>
          </div>
        </div>

        {/* Tooltip */}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-gray-900 text-white text-xs rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[60]">
          {tooltipText}
          <div className="absolute -translate-x-1/2 border-4 border-transparent left-1/2 top-full border-t-gray-900" />
        </div>
      </div>
      <CurrencyDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        breakdown={breakdown}
        title={title}
        total={amount}
        prefferedCurrency={prefferedCurrency}
      />
    </>
  );
};
